"use client";

import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { baseName, formatBytes } from "@/lib/tool-utils";
const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB per PDF

type MergeItem = {
  id: string;
  file: File;
  pageCount: number;
};
type PdfMode = "merge" | "split";
type Range = {
  start: number;
  end: number;
};
function isPdf(file: File) {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

/** Loads a PDF and turns pdf-lib's errors into messages a person can act on. */
async function loadPdf(file: File): Promise<PDFDocument> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(`${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_FILE_BYTES)}.`);
  }
  try {
    return await PDFDocument.load(await file.arrayBuffer());
  } catch (error) {
    const message = error instanceof Error ? error.message.toLowerCase() : "";
    if (message.includes("encrypted")) {
      throw new Error(`${file.name} is password-protected. Remove the password first, then add it again.`);
    }
    throw new Error(`${file.name} could not be read. It may be damaged or not a real PDF.`);
  }
}
function toPdfBlob(bytes: Uint8Array) {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return new Blob([copy], {
    type: "application/pdf"
  });
}

/**
 * Parses "1-3, 5, 7-10" into ranges. Throws a clear message on bad input.
 */
function parseRangeInput(raw: string, totalPages: number): Range[] {
  const parts = raw.split(",").map(part => part.trim()).filter(Boolean);
  if (parts.length === 0) throw new Error("Enter at least one page or range, like 1-3, 5.");
  return parts.map(part => {
    const match = /^(\d+)(?:\s*-\s*(\d+))?$/.exec(part);
    if (!match) {
      throw new Error(`"${part}" isn't a valid page or range. Use formats like 5 or 2-6.`);
    }
    const start = Number(match[1]);
    const end = match[2] ? Number(match[2]) : start;
    if (start < 1 || end < 1) throw new Error(`Page numbers start at 1 ("${part}").`);
    if (end < start) throw new Error(`"${part}" runs backwards. Write it as ${end}-${start}.`);
    if (start > totalPages) {
      throw new Error(`Page ${start} doesn't exist. This PDF has ${totalPages} page${totalPages === 1 ? "" : "s"}.`);
    }
    if (end > totalPages) {
      throw new Error(`Page ${end} doesn't exist. This PDF has ${totalPages} page${totalPages === 1 ? "" : "s"}.`);
    }
    return {
      start,
      end
    };
  });
}
export default function PdfMergeSplit({}: {
  toolName: string;
}) {
  const mergeInputRef = useRef<HTMLInputElement>(null);
  const splitInputRef = useRef<HTMLInputElement>(null);
  const mergedUrlRef = useRef("");
  const zipUrlRef = useRef("");
  // Separate counters so the two drop zones can never interfere with each other.
  const mergeDragDepth = useRef(0);
  const splitDragDepth = useRef(0);
  const [mode, setMode] = useState<PdfMode>("merge");

  // Merge state
  const [mergeFiles, setMergeFiles] = useState<MergeItem[]>([]);
  const [mergedDownloadUrl, setMergedDownloadUrl] = useState("");
  const [draggedMergeId, setDraggedMergeId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [mergeDragActive, setMergeDragActive] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeMessage, setMergeMessage] = useState("");
  const [mergeError, setMergeError] = useState("");

  // Split state
  const [splitFile, setSplitFile] = useState<File | null>(null);
  const [splitPageCount, setSplitPageCount] = useState(0);
  const [splitRanges, setSplitRanges] = useState("");
  const [splitEveryN, setSplitEveryN] = useState("");
  const [splitZipUrl, setSplitZipUrl] = useState("");
  const [splitDragActive, setSplitDragActive] = useState(false);
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitStatus, setSplitStatus] = useState("Upload a PDF to get started.");
  const [splitError, setSplitError] = useState("");

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      if (mergedUrlRef.current) URL.revokeObjectURL(mergedUrlRef.current);
      if (zipUrlRef.current) URL.revokeObjectURL(zipUrlRef.current);
    };
  }, []);
  const replaceMergedUrl = useCallback((next: string) => {
    if (mergedUrlRef.current) URL.revokeObjectURL(mergedUrlRef.current);
    mergedUrlRef.current = next;
    setMergedDownloadUrl(next);
  }, []);
  const replaceZipUrl = useCallback((next_0: string) => {
    if (zipUrlRef.current) URL.revokeObjectURL(zipUrlRef.current);
    zipUrlRef.current = next_0;
    setSplitZipUrl(next_0);
  }, []);

  /* ---------------------------------- Merge --------------------------------- */

  async function addMergeFiles(nextFiles: FileList | File[]) {
    const all = Array.from(nextFiles);
    const pdfs = all.filter(isPdf);
    const skippedNonPdf = all.length - pdfs.length;
    setMergeError("");
    setMergeMessage("");
    if (pdfs.length === 0) {
      setMergeError("Those files aren't PDFs. Please choose .pdf files.");
      return;
    }
    setMergeBusy(true);

    // allSettled: one bad file must not throw away the good ones.
    const results = await Promise.allSettled(pdfs.map(async file => {
      const pdf = await loadPdf(file);
      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        pageCount: pdf.getPageCount()
      } satisfies MergeItem;
    }));
    const added: MergeItem[] = [];
    const problems: string[] = [];
    for (const result of results) {
      if (result.status === "fulfilled") added.push(result.value);else problems.push(result.reason instanceof Error ? result.reason.message : "A file could not be read.");
    }
    if (skippedNonPdf > 0) {
      problems.push(`${skippedNonPdf} non-PDF file${skippedNonPdf === 1 ? " was" : "s were"} skipped.`);
    }
    if (added.length > 0) {
      setMergeFiles(previous => [...previous, ...added]);
      replaceMergedUrl(""); // the file list changed, so the old result is stale
      setMergeMessage(`Added ${added.length} PDF${added.length === 1 ? "" : "s"}.`);
    }
    if (problems.length > 0) setMergeError(problems.join(" "));
    setMergeBusy(false);
  }
  function moveMergeFile(id: string, direction: -1 | 1) {
    setMergeFiles(previous_0 => {
      const index = previous_0.findIndex(entry => entry.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= previous_0.length) return previous_0;
      const next_1 = [...previous_0];
      [next_1[index], next_1[target]] = [next_1[target], next_1[index]];
      return next_1;
    });
    replaceMergedUrl("");
  }
  function reorderMergeFiles(draggedId: string, targetId: string) {
    if (draggedId === targetId) return;
    setMergeFiles(previous_1 => {
      const draggedIndex = previous_1.findIndex(entry_0 => entry_0.id === draggedId);
      const targetIndex = previous_1.findIndex(entry_1 => entry_1.id === targetId);
      if (draggedIndex < 0 || targetIndex < 0) return previous_1;
      const next_2 = [...previous_1];
      const [draggedItem] = next_2.splice(draggedIndex, 1);
      next_2.splice(targetIndex, 0, draggedItem);
      return next_2;
    });
    replaceMergedUrl("");
  }
  function removeMergeFile(id_0: string) {
    setMergeFiles(previous_2 => previous_2.filter(entry_2 => entry_2.id !== id_0));
    replaceMergedUrl("");
    setMergeMessage("");
  }
  function clearMerge() {
    setMergeFiles([]);
    replaceMergedUrl("");
    setMergeMessage("");
    setMergeError("");
  }
  async function mergePdfFiles() {
    if (mergeFiles.length < 2) {
      setMergeError("Add at least two PDFs to merge.");
      return;
    }
    setMergeBusy(true);
    setMergeError("");
    setMergeMessage("Combining your PDFs…");
    try {
      const mergedPdf = await PDFDocument.create();
      for (const item of mergeFiles) {
        const sourcePdf = await loadPdf(item.file);
        const copiedPages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
        copiedPages.forEach(page => mergedPdf.addPage(page));
      }
      const blob = toPdfBlob(await mergedPdf.save());
      replaceMergedUrl(URL.createObjectURL(blob));
      setMergeMessage(`Done. Your merged PDF has ${mergedPdf.getPageCount()} pages (${formatBytes(blob.size)}).`);
    } catch (error) {
      setMergeMessage("");
      setMergeError(error instanceof Error ? error.message : "Something went wrong while merging.");
    } finally {
      setMergeBusy(false);
    }
  }

  /* ---------------------------------- Split --------------------------------- */

  async function loadSplitPdf(file_0: File) {
    setSplitError("");
    if (!isPdf(file_0)) {
      setSplitError("That file isn't a PDF. Please choose a .pdf file.");
      return;
    }
    setSplitBusy(true);
    setSplitStatus("Reading your PDF…");
    try {
      const pdf_0 = await loadPdf(file_0);
      setSplitFile(file_0);
      setSplitPageCount(pdf_0.getPageCount());
      replaceZipUrl("");
      setSplitStatus(`${pdf_0.getPageCount()} page${pdf_0.getPageCount() === 1 ? "" : "s"} loaded.`);
    } catch (error_0) {
      setSplitStatus("Upload a PDF to get started.");
      setSplitError(error_0 instanceof Error ? error_0.message : "That PDF could not be read.");
    } finally {
      setSplitBusy(false);
    }
  }
  function clearSplit() {
    setSplitFile(null);
    setSplitPageCount(0);
    setSplitRanges("");
    setSplitEveryN("");
    replaceZipUrl("");
    setSplitError("");
    setSplitStatus("Upload a PDF to get started.");
  }
  async function splitPdfFile() {
    if (!splitFile) return;
    setSplitError("");
    setSplitBusy(true);
    setSplitStatus("Splitting…");
    try {
      const sourcePdf_0 = await loadPdf(splitFile);
      const totalPages = sourcePdf_0.getPageCount();
      let groups: Range[] = [];
      if (splitRanges.trim()) {
        groups = parseRangeInput(splitRanges, totalPages);
      } else if (splitEveryN.trim()) {
        const everyN = Number(splitEveryN);
        if (!Number.isInteger(everyN) || everyN < 1) {
          throw new Error("Split every N pages needs a whole number of 1 or more.");
        }
        for (let start = 1; start <= totalPages; start += everyN) {
          groups.push({
            start,
            end: Math.min(start + everyN - 1, totalPages)
          });
        }
      } else {
        for (let page_0 = 1; page_0 <= totalPages; page_0 += 1) groups.push({
          start: page_0,
          end: page_0
        });
      }
      if (groups.length > 500) {
        throw new Error("That would create more than 500 files. Use larger ranges.");
      }
      const zip = new JSZip();
      const stem = baseName(splitFile.name);
      const pad = String(groups.length).length;
      for (const [index_0, range] of groups.entries()) {
        const part = await PDFDocument.create();
        const indices = Array.from({
          length: range.end - range.start + 1
        }, (_, offset) => range.start - 1 + offset);
        const pages = await part.copyPages(sourcePdf_0, indices);
        pages.forEach(page_1 => part.addPage(page_1));
        const label = range.start === range.end ? `page-${range.start}` : `pages-${range.start}-${range.end}`;
        zip.file(`${stem}_${String(index_0 + 1).padStart(pad, "0")}_${label}.pdf`, await part.save());
      }
      const zipBlob = await zip.generateAsync({
        type: "blob"
      });
      replaceZipUrl(URL.createObjectURL(zipBlob));
      setSplitStatus(`Done. ${groups.length} file${groups.length === 1 ? "" : "s"} ready in a ZIP (${formatBytes(zipBlob.size)}).`);
    } catch (error_1) {
      setSplitStatus("Fix the problem below and try again.");
      setSplitError(error_1 instanceof Error ? error_1.message : "Something went wrong while splitting.");
    } finally {
      setSplitBusy(false);
    }
  }

  /* ---------------------------------- Render -------------------------------- */

  const totalMergePages = mergeFiles.reduce((sum, item_0) => sum + item_0.pageCount, 0);
  const bothSplitFieldsFilled = splitRanges.trim() !== "" && splitEveryN.trim() !== "";
  const zoneClass = (active: boolean) => `cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-indigo-300 ${active ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-slate-50 hover:border-indigo-400"}`;
  const handleMergeDragEnter = (event: DragEvent) => {
    event.preventDefault();
    mergeDragDepth.current += 1;
    setMergeDragActive(true);
  };
  const handleMergeDragLeave = () => {
    mergeDragDepth.current = Math.max(0, mergeDragDepth.current - 1);
    if (mergeDragDepth.current === 0) setMergeDragActive(false);
  };
  const handleMergeDrop = (event_0: DragEvent) => {
    event_0.preventDefault();
    mergeDragDepth.current = 0;
    setMergeDragActive(false);
    if (event_0.dataTransfer.files.length > 0) {
      void addMergeFiles(event_0.dataTransfer.files);
    }
  };
  const handleSplitDragEnter = (event_1: DragEvent) => {
    event_1.preventDefault();
    splitDragDepth.current += 1;
    setSplitDragActive(true);
  };
  const handleSplitDragLeave = () => {
    splitDragDepth.current = Math.max(0, splitDragDepth.current - 1);
    if (splitDragDepth.current === 0) setSplitDragActive(false);
  };
  const handleSplitDrop = (event_2: DragEvent) => {
    event_2.preventDefault();
    splitDragDepth.current = 0;
    setSplitDragActive(false);
    const file_1 = event_2.dataTransfer.files[0];
    if (file_1) void loadSplitPdf(file_1);
  };
  return <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="PDF tool mode">
        {(["merge", "split"] as const).map(value => <button key={value} type="button" role="tab" aria-selected={mode === value} onClick={() => setMode(value)} className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${mode === value ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
            {value === "merge" ? "Merge" : "Split"}
          </button>)}
        <span className="ml-auto inline-flex items-center rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
          100% private — processed in your browser
        </span>
      </div>

      {mode === "merge" ? <div className="space-y-4">
          <div role="button" tabIndex={0} aria-label="Add PDFs: click to browse or drag files here" onClick={() => mergeInputRef.current?.click()} onKeyDown={event_3 => {
        if (event_3.key === "Enter" || event_3.key === " ") {
          event_3.preventDefault();
          mergeInputRef.current?.click();
        }
      }} onDragEnter={handleMergeDragEnter} onDragOver={event_4 => event_4.preventDefault()} onDragLeave={handleMergeDragLeave} onDrop={handleMergeDrop} className={zoneClass(mergeDragActive)}>
            <p className="text-base font-semibold text-slate-900">
              {mergeDragActive ? "Drop to add PDFs" : "Drop PDF files here, or click to choose"}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              Add two or more, then set the order. Files are combined top to bottom.
            </p>
            <span className="mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
              Choose PDFs
            </span>
            <input ref={mergeInputRef} type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={event_5 => {
          if (event_5.target.files) {
            void addMergeFiles(event_5.target.files);
            event_5.target.value = "";
          }
        }} />
          </div>

          {mergeError && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {mergeError}
            </p>}

          <ul className="space-y-3" aria-label="PDFs to merge, in order">
            {mergeFiles.map((item_1, index_1) => <li key={item_1.id} draggable onDragStart={() => setDraggedMergeId(item_1.id)} onDragEnd={() => {
          setDraggedMergeId(null);
          setDropTargetId(null);
        }} onDragOver={event_6 => {
          event_6.preventDefault();
          setDropTargetId(item_1.id);
        }} onDrop={event_7 => {
          event_7.preventDefault();
          event_7.stopPropagation();
          if (draggedMergeId) reorderMergeFiles(draggedMergeId, item_1.id);
          setDraggedMergeId(null);
          setDropTargetId(null);
        }} className={`rounded-xl border p-4 transition ${dropTargetId === item_1.id && draggedMergeId && draggedMergeId !== item_1.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-slate-50"} ${draggedMergeId === item_1.id ? "opacity-50" : ""}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                      {index_1 + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{item_1.file.name}</p>
                      <p className="text-xs text-slate-500">
                        {item_1.pageCount} page{item_1.pageCount === 1 ? "" : "s"} · {formatBytes(item_1.file.size)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => moveMergeFile(item_1.id, -1)} disabled={index_1 === 0} aria-label={`Move ${item_1.file.name} up`} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40">
                      ↑
                    </button>
                    <button type="button" onClick={() => moveMergeFile(item_1.id, 1)} disabled={index_1 === mergeFiles.length - 1} aria-label={`Move ${item_1.file.name} down`} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40">
                      ↓
                    </button>
                    <button type="button" onClick={() => removeMergeFile(item_1.id)} aria-label={`Remove ${item_1.file.name}`} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white">
                      Remove
                    </button>
                  </div>
                </div>
              </li>)}

            {mergeFiles.length === 0 && <li className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                Added files will appear here in the merge order. Drag them, or use the arrows, to reorder.
              </li>}
          </ul>

          {mergeFiles.length > 0 && <p className="text-xs text-slate-500">
              {mergeFiles.length} file{mergeFiles.length === 1 ? "" : "s"} · {totalMergePages} pages in total
            </p>}

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => void mergePdfFiles()} disabled={mergeFiles.length < 2 || mergeBusy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300">
              {mergeBusy ? "Working…" : "Combine into one PDF"}
            </button>

            {mergedDownloadUrl && <a href={mergedDownloadUrl} download="merged-document.pdf" className="inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
                Download merged PDF
              </a>}

            {mergeFiles.length > 0 && <button type="button" onClick={clearMerge} disabled={mergeBusy} className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50">
                Clear all
              </button>}
          </div>

          {mergeFiles.length === 1 && !mergeError && <p className="text-sm text-slate-500">Add at least one more PDF to enable merging.</p>}
          {mergeMessage && <p aria-live="polite" className="text-sm text-slate-600">
              {mergeMessage}
            </p>}
        </div> : <div className="space-y-4">
          <div role="button" tabIndex={0} aria-label="Add a PDF to split: click to browse or drag a file here" onClick={() => splitInputRef.current?.click()} onKeyDown={event_8 => {
        if (event_8.key === "Enter" || event_8.key === " ") {
          event_8.preventDefault();
          splitInputRef.current?.click();
        }
      }} onDragEnter={handleSplitDragEnter} onDragOver={event_9 => event_9.preventDefault()} onDragLeave={handleSplitDragLeave} onDrop={handleSplitDrop} className={zoneClass(splitDragActive)}>
            <p className="text-base font-semibold text-slate-900">
              {splitDragActive ? "Drop your PDF" : splitFile ? "Drop or click to replace this PDF" : "Drop a PDF here, or click to choose"}
            </p>
            <p className="mt-2 text-sm text-slate-600">One PDF at a time, up to {formatBytes(MAX_FILE_BYTES)}.</p>
            <span className="mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
              Choose PDF
            </span>
            <input ref={splitInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={event_10 => {
          const file_2 = event_10.target.files?.[0];
          if (file_2) void loadSplitPdf(file_2);
          event_10.target.value = "";
        }} />
          </div>

          {splitFile && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{splitFile.name}</p>
                <p className="mt-1">
                  {splitPageCount} page{splitPageCount === 1 ? "" : "s"} · {formatBytes(splitFile.size)}
                </p>
              </div>
              <button type="button" onClick={clearSplit} disabled={splitBusy} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white disabled:opacity-50">
                Remove
              </button>
            </div>}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              Page ranges
              <input type="text" value={splitRanges} onChange={event_11 => {
            setSplitRanges(event_11.target.value);
            setSplitError("");
            replaceZipUrl("");
          }} placeholder="e.g. 1-3, 5, 7-10" disabled={!splitFile} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100" />
              <span className="mt-1 block text-xs text-slate-500">Each range becomes its own PDF.</span>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Split every N pages
              <input type="number" min="1" step="1" inputMode="numeric" value={splitEveryN} onChange={event_12 => {
            setSplitEveryN(event_12.target.value);
            setSplitError("");
            replaceZipUrl("");
          }} placeholder="e.g. 2" disabled={!splitFile} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100" />
              <span className="mt-1 block text-xs text-slate-500">
                Leave both empty to make one PDF per page.
              </span>
            </label>
          </div>

          {bothSplitFieldsFilled && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              You filled in both options. Page ranges will be used and “every N pages” will be ignored.
            </p>}

          {splitError && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {splitError}
            </p>}

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => void splitPdfFile()} disabled={!splitFile || splitBusy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300">
              {splitBusy ? "Working…" : "Split and prepare ZIP"}
            </button>

            {splitZipUrl && <a href={splitZipUrl} download={`${baseName(splitFile?.name || "split-file")}-split.zip`} className="inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
                Download ZIP
              </a>}
          </div>

          <p aria-live="polite" className="text-sm text-slate-600">
            {splitStatus}
          </p>
        </div>}
    </div>;
}